import { useState, useEffect, useMemo } from "react";
import usePageTitle from "@/hooks/usePageTitle";
import Table, { TableHeading, TableControls, TableFooter } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { DuesRatesRow, Variables, StatusType } from "./types";
import { DuesRates, PreviousPricing } from "@/types";
import { useQuery } from "@apollo/client";
import { GET_DUES_RATES_QUERY, GET_DUES_CYCLE_QUERY } from "./queries";
import { centsToDollarsFormatted } from "@/lib/currencyHelpers";
import { toFormat } from "@/lib/dateHelpers";
import { sortBy } from "lodash";
import SearchInput from "@/components/SearchInput";
import { useSearchWithParams, useParams } from "@/hooks/useSearchParams";
import { GET_CLASSIFICATIONS } from "../../../queries";
import { ClassificationType } from "../../types";
import MultiSelect from "@/components/MultiSelect";
import { get } from "lodash";
import { getColumns } from "./_components/TableColumns";
import MetricGrid from "@/components/MetricGrid";
import SectionAutocomplete from "./_components/SectionAutocomplete";
import usePagination from "@/hooks/usePagination";
import { DOMAIN, PERMISSION, useRequireCapability } from "@/hooks/useCapabilities";
import { MaterialNames } from "@/components/material_symbol_names";
import Banner from "@/components/Banner";
import SectionHeading from "@/components/SectionHeading";

type DuesRatesData = {
  duesRates: {
    nodes: DuesRates[];
    totalCount: number;
    pageInfo: {
      hasNextPage: boolean;
      hasPreviousPage: boolean;
      startCursor: string;
      endCursor: string;
    };
  };
};

const getPriceByYear = (previousPricing: PreviousPricing[], year: string) => {
  return centsToDollarsFormatted(previousPricing?.filter((p) => p.year === year)?.[0]?.priceCents);
};

const formatTableData = ({ data, currentYear }: { data: DuesRates[]; currentYear: number }) => {
  return data.map(
    ({
      id,
      classification,
      classificationDescription,
      productName,
      memberGrade,
      updatedAt,
      priceCents,
      previousPricing,
      finalized,
      bypassValidation
    }) => {
      const sectionIndex = productName?.indexOf("Section") ?? -1;
      const sectionName = productName?.substring(0, sectionIndex)?.trim() || "National";

      return {
        id,
        productName: `${memberGrade} ${productName} `,
        classification,
        classificationDescription,
        updatedAt: toFormat(updatedAt),
        rate_current_year: centsToDollarsFormatted(priceCents),
        rate_previous_year: getPriceByYear(previousPricing, `${currentYear - 1}`),
        rate_previous_year_2: getPriceByYear(previousPricing, `${currentYear - 2}`),
        classificationNumber: Number(classification?.split("-")?.[1]),
        classificationLetter: classification?.split("-")?.[0],
        section: sectionName,
        finalized,
        status: bypassValidation ? StatusType.WARNING : null,
        statusMessage: bypassValidation ? ["This rate has been approved to exceed Constitutional rules"] : []
      };
    }
  );
};

export default function AnnualSectionRatesPage() {
  usePageTitle(
    "/billing/dues-management/manage-products/annual-section-rates",
    "Billing | Dues Management | Manage Products | Annual Section Rates"
  );
  useRequireCapability({ domain: DOMAIN.COMMERCE, permission: PERMISSION.viewDuesRate });
  const defaultVariables = {
    kind: "section",
    finalized: true
  };

  const { params, addSearchParam, deleteSearchParams } = useParams();
  const [variables, setVariables] = useState<Variables>({ ...defaultVariables });
  const [selectedClassificationValues, setSelectedClassificationValues] = useState<string[]>(
    params.classifications || []
  );
  const [selectedValidationValues, setSelectedValidationValues] = useState<string[]>(params.validation || []);
  const [sectionSlug, setSectionSlug] = useState<string>(params.sectionSlug || "section");
  const [sectionValue, setSectionValue] = useState<string | null>(params.section || null);
  const { paginationVariables, createHeaderPagination, createFooterPagination } = usePagination({
    defaultPageSize: 50
  });
  const currentYear = new Date().getFullYear();

  const { data: classificationData } = useQuery(GET_CLASSIFICATIONS);
  const { data, loading } = useQuery<DuesRatesData>(GET_DUES_RATES_QUERY, {
    variables: {
      filters: {
        ...variables
      },
      ...paginationVariables
    },
    fetchPolicy: "cache-first"
  });
  const { data: duesCycleData, loading: duesCycleLoading } = useQuery(GET_DUES_CYCLE_QUERY, {
    variables: {
      year: currentYear
    },
    errorPolicy: "all"
  });

  const tableData: DuesRatesRow[] = sortBy(
    formatTableData({ data: data?.duesRates?.nodes || [], currentYear }),
    (item) => [item.section]
  );
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });
  const filters = {
    classifications: selectedClassificationValues.length > 0,
    validation: selectedValidationValues.length > 0,
    section: sectionSlug !== "section" && sectionSlug !== "all"
  };

  const activeFilterCount = Object.values(filters).filter(Boolean).length;
  const hasActiveFilters = activeFilterCount > 0 || !!searchValue;

  const classificationNodes = get(classificationData, "classifications.nodes");
  const classificationDataNodes =
    classificationNodes?.map((node: ClassificationType) => ({
      value: node.name,
      label: node.name
    })) || [];

  useEffect(() => {
    setVariables((prevVariables) => ({
      ...prevVariables,
      search: debouncedSearch || undefined,
      kind: sectionSlug,
      finalized: true,
      classification: selectedClassificationValues.length > 0 ? selectedClassificationValues : undefined,
      validation: selectedValidationValues.length > 0 ? selectedValidationValues : undefined
    }));
  }, [debouncedSearch, sectionSlug, selectedClassificationValues, selectedValidationValues]);

  const columns = useMemo(() => {
    return getColumns({
      tableData,
      currentYear
    });
  }, [tableData, currentYear]);

  const duesCycle = get(duesCycleData, "duesCycle", null);
  const {
    finalizedSectionNames = [],
    finalizedSectionCount = 0,
    unfinalizedSectionNames = [],
    unfinalizedSectionCount = 0,
    totalSectionCount = 0
  } = duesCycle ?? {};

  const hasNoDuesCycleData = !duesCycleLoading && !duesCycle;

  return (
    <>
      {hasNoDuesCycleData && (
        <Banner
          description={`No dues cycle data available for ${currentYear}. The metrics below may not reflect current information.`}
          variant="warn"
          inline
        />
      )}
      <SectionHeading heading={`Status for Member Year ${currentYear}-${currentYear + 1}`} />
      <MetricGrid
        metrics={[
          {
            label: `Total Sections`,
            value: `${totalSectionCount}`,
            loading: duesCycleLoading,
            iconName: "domain" as MaterialNames
          },
          {
            label: `Sections with Finalized Rates`,
            value: `${finalizedSectionCount}`,
            loading: duesCycleLoading,
            iconName: "domain" as MaterialNames,
            ...(finalizedSectionCount > 0 && {
              tooltip: {
                title: "Sections with Finalized Rates",
                supportingText: finalizedSectionNames.join("\r\n") || "--"
              }
            })
          },
          {
            label: `Sections with Unfinalized Rates`,
            value: `${unfinalizedSectionCount}`,
            loading: duesCycleLoading,
            variant: "error",
            iconName: "domain" as MaterialNames,
            ...(unfinalizedSectionCount > 0 && {
              tooltip: {
                title: "Sections with Unfinalized Rates",
                supportingText: unfinalizedSectionNames.join("\r\n") || "--"
              }
            })
          }
        ]}
        layout="expanded"
        hasHeading
      />

      <TableHeading
        heading="Sections Annual Dues and Fees"
        body="Sections with finalized rates are shown below. Only an upcoming billing cycle can be edited, previous annual prices are shown for convenience."
        actions={[
          {
            label: "Reopen Section Rates",
            icon: "lock_open",
            variant: "outlined",
            onClick: () => console.log("reopen"),
            disabled: true
          }
        ]}
      />
      <Table
        data={tableData}
        loading={loading}
        columns={columns}
        renderControls={() => (
          <TableControls
            pagination={createHeaderPagination(data?.duesRates)}
            useExpandedFilters
            expandedFiltersState={filters}
            onClearAllFilters={() => {
              setVariables(defaultVariables);
              setSelectedClassificationValues([]);
              setSelectedValidationValues([]);
              setSectionSlug("section");
              setSectionValue(null);
              clearSearch();
              deleteSearchParams(["classifications[]", "validation[]", "section", "sectionSlug"]);
            }}
            activeFilters={activeFilterCount}
          >
            <SearchInput
              placeholder="Search Product"
              query={searchValue}
              onChange={setSearchValue}
              onClear={clearSearch}
            />
            <div data-name="section" data-filter-size="custom" className="min-w-[280px]">
              <SectionAutocomplete
                value={sectionValue}
                onChange={(value, slug) => {
                  setSectionValue(value);
                  setSectionSlug(slug);
                  addSearchParam("section", value);
                  addSearchParam("sectionSlug", slug);
                }}
                onReset={() => {
                  setSectionValue(null);
                  setSectionSlug("section");
                  deleteSearchParams(["section", "sectionSlug"]);
                }}
              />
            </div>
            <div data-name="classifications">
              <MultiSelect
                label="Classification"
                selectedValues={selectedClassificationValues}
                options={classificationDataNodes}
                onSelect={(value, checked) => {
                  const classification = checked
                    ? [...selectedClassificationValues, value]
                    : selectedClassificationValues.filter((v) => v !== value);
                  setSelectedClassificationValues(classification);
                  if (classification.length > 0) {
                    addSearchParam("classifications[]", classification.join(","));
                  } else {
                    deleteSearchParams("classifications[]");
                  }
                }}
                onClear={() => {
                  setSelectedClassificationValues([]);
                  deleteSearchParams("classifications[]");
                }}
                className="w-full"
              />
            </div>
            <div data-name="validation">
              <MultiSelect
                label="Validation"
                selectedValues={selectedValidationValues}
                options={[
                  {
                    value: "ok",
                    label: "OK"
                  },
                  {
                    value: "warning",
                    label: "Warning"
                  }
                ]}
                onSelect={(value, checked) => {
                  const validation = checked
                    ? [...selectedValidationValues, value]
                    : selectedValidationValues.filter((v) => v !== value);
                  setSelectedValidationValues(validation);
                  if (validation.length > 0) {
                    addSearchParam("validation[]", validation.join(","));
                  } else {
                    deleteSearchParams("validation[]");
                  }
                }}
                onClear={() => {
                  setSelectedValidationValues([]);
                  deleteSearchParams("validation[]");
                }}
                className="w-full"
              />
            </div>
          </TableControls>
        )}
        hasActiveFilters={hasActiveFilters}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Annual Section Rates"
            caption="There are no annual section rates to display"
            iconName="request_quote"
          />
        )}
        renderFooter={() => (
          <TableFooter pagination={createFooterPagination(data?.duesRates)} totalRowCount={tableData.length} />
        )}
      />
    </>
  );
}
